import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Switch,
    FormControlLabel,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Tooltip
} from '@mui/material';
import { Save as SaveIcon, WifiTethering as WifiTetheringIcon, Refresh as RefreshIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig, testAIConnection, getAiUsage } from '../../../redux/slices/config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SettingsCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const ActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
}));

const CheckList = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

const CheckRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.75),
    '&:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

const CheckBody = styled(Box)(() => ({
    minWidth: 0,
}));

const UsageHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
}));

const UsageUserCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const UsageCard = styled(SettingsCard)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const UsageControls = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const UsageWindowSelect = styled(FormControl)({
    minWidth: 120,
});

const UsageEmptyMessage = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    textAlign: 'center',
}));

const UsageAvatar = styled(Avatar)({
    width: 28,
    height: 28,
});

const AIConfig = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testChecks, setTestChecks] = useState(null);
    const [formData, setFormData] = useState({
        enabled: false,
        provider: 'gemini',
        containerName: '',
        model: '',
        debug: false,
        userConcurrencyCap: 5,
    });
    const [usageDays, setUsageDays] = useState(7);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageRows, setUsageRows] = useState([]);
    const [usageDates, setUsageDates] = useState([]);

    useEffect(() => {
        loadConfig();
    }, []);

    useEffect(() => {
        loadUsage(usageDays);
    }, [usageDays]);

    const loadUsage = (days) => {
        setUsageLoading(true);
        dispatch(getAiUsage({
            days,
            callback: (data) => {
                setUsageLoading(false);
                if (!data) return;
                setUsageRows(data.rows || []);
                setUsageDates(data.dates || []);
            }
        }));
    };

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'aiConfig',
            value: null,
            callback: (data) => {
                if (data?.value) {
                    const val = data.value;
                    setFormData({
                        enabled: !!val.enabled,
                        provider: val.provider || 'gemini',
                        containerName: val.containerName || '',
                        model: val.model || '',
                        debug: !!val.debug,
                        userConcurrencyCap: Number.isFinite(val.userConcurrencyCap) && val.userConcurrencyCap > 0
                            ? val.userConcurrencyCap
                            : 5,
                    });
                }
                setLoading(false);
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleTest = () => {
        setTesting(true);
        setTestChecks(null);
        dispatch(testAIConnection({
            containerName: formData.containerName,
            model: formData.model,
            // Sent from the form, not the saved config, so a provider can be tested before saving.
            provider: formData.provider,
            callback: (data) => {
                setTestChecks(Array.isArray(data?.checks) && data.checks.length ? data.checks : null);
                if (data?.success) {
                    dispatch(showAlert({ severity: 'success', message: data.message || 'AI container is reachable' }));
                } else {
                    dispatch(showAlert({ severity: 'error', message: data?.message || 'Connection test failed' }));
                }
                setTesting(false);
            }
        }));
    };

    const handleSave = () => {
        setSaving(true);
        dispatch(saveConfig({
            key: 'aiConfig',
            value: {
                enabled: formData.enabled,
                provider: formData.provider || 'gemini',
                containerName: formData.containerName,
                model: formData.model,
                debug: formData.debug,
                userConcurrencyCap: Number(formData.userConcurrencyCap) > 0 ? Number(formData.userConcurrencyCap) : 5,
            },
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(showAlert({ severity: 'success', message: 'AI configuration saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage AI gateway configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SettingsCard>
                        <Typography variant="h6" gutterBottom>
                            AI Gateway Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="enabled"
                                            checked={formData.enabled}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Enable AI Gateway"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="debug"
                                            checked={formData.debug}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Debug Mode (capture and log exec output)"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="ai-provider-label">Provider</InputLabel>
                                    <Select
                                        labelId="ai-provider-label"
                                        label="Provider"
                                        name="provider"
                                        value={formData.provider || 'gemini'}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="gemini">Gemini</MenuItem>
                                        <MenuItem value="claude">Claude</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Container Name"
                                    name="containerName"
                                    value={formData.containerName}
                                    onChange={handleChange}
                                    placeholder={formData.provider === 'claude' ? 'claude-cli' : 'gemini-cli'}
                                    helperText="Docker container name or ID for docker exec"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Model"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleChange}
                                    placeholder={formData.provider === 'claude' ? 'claude-sonnet-4-6' : 'gemini-2.0-flash'}
                                    helperText={formData.provider === 'claude'
                                        ? 'Claude model id passed to the CLI via --model (e.g. claude-sonnet-4-6, claude-opus-4-7)'
                                        : 'Gemini model string passed to the CLI via --model (e.g. gemini-2.0-flash, gemini-2.5-pro)'}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Per-user concurrency cap"
                                    name="userConcurrencyCap"
                                    value={formData.userConcurrencyCap}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, max: 50, step: 1 }}
                                    helperText="Max simultaneous in-flight AI dispatches per user (Redis-keyed). Overflow is dropped silently. Defaults to 5."
                                />
                            </Grid>
                        </Grid>
                    </SettingsCard>

                    <ActionsRow>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<WifiTetheringIcon />}
                            onClick={handleTest}
                            disabled={!formData.containerName || testing}
                        >
                            {testing ? 'Testing...' : 'Test Connection'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>

                    {testChecks && (
                        <CheckList>
                            {testChecks.map((check) => (
                                <CheckRow key={check.name}>
                                    {check.ok
                                        ? <CheckCircleIcon fontSize="small" color="success" />
                                        : <CancelIcon fontSize="small" color="error" />}
                                    <CheckBody>
                                        <Typography variant="body2" fontWeight={600}>{check.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">{check.detail}</Typography>
                                    </CheckBody>
                                </CheckRow>
                            ))}
                        </CheckList>
                    )}

                    <UsageCard>
                        <UsageHeader>
                            <Box>
                                <Typography variant="h6">AI Usage</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Per-user AI gateway call counts over the selected window
                                </Typography>
                            </Box>
                            <UsageControls>
                                <UsageWindowSelect size="small">
                                    <InputLabel id="ai-usage-days-label">Window</InputLabel>
                                    <Select
                                        labelId="ai-usage-days-label"
                                        label="Window"
                                        value={usageDays}
                                        onChange={(e) => setUsageDays(Number(e.target.value))}
                                    >
                                        <MenuItem value={1}>Today</MenuItem>
                                        <MenuItem value={7}>Last 7 days</MenuItem>
                                        <MenuItem value={14}>Last 14 days</MenuItem>
                                        <MenuItem value={30}>Last 30 days</MenuItem>
                                        <MenuItem value={90}>Last 90 days</MenuItem>
                                    </Select>
                                </UsageWindowSelect>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => loadUsage(usageDays)}
                                    disabled={usageLoading}
                                >
                                    Refresh
                                </Button>
                            </UsageControls>
                        </UsageHeader>

                        {usageLoading ? (
                            <LoadingRow>
                                <CircularProgress />
                            </LoadingRow>
                        ) : usageRows.length === 0 ? (
                            <UsageEmptyMessage variant="body2" color="text.secondary">
                                No AI calls recorded in this window.
                            </UsageEmptyMessage>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>User</strong></TableCell>
                                            <TableCell align="right"><strong>Total</strong></TableCell>
                                            {usageDates.map((d) => (
                                                <TableCell key={d} align="right">
                                                    <Tooltip title={d}>
                                                        <span>{d.slice(5)}</span>
                                                    </Tooltip>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {usageRows.map((row) => {
                                            const fullName = row.user
                                                ? (row.user.fullName || `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim() || row.user.email)
                                                : row.userId;
                                            return (
                                                <TableRow key={row.userId} hover>
                                                    <TableCell>
                                                        <UsageUserCell>
                                                            <UsageAvatar
                                                                src={row.user?.avatar || undefined}
                                                                alt={fullName}
                                                            >
                                                                {fullName?.[0]?.toUpperCase() || '?'}
                                                            </UsageAvatar>
                                                            <Box>
                                                                <Typography variant="body2">{fullName}</Typography>
                                                                {row.user?.email && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {row.user.email}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </UsageUserCell>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {row.total}
                                                        </Typography>
                                                    </TableCell>
                                                    {usageDates.map((d) => (
                                                        <TableCell key={d} align="right">
                                                            {row.byDate?.[d] || 0}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </UsageCard>
                </>
            )}
            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default AIConfig;
